import { useEffect, useRef, useState } from 'react';

import { IconCheck, IconChevronDown, IconFilter } from './icons';
import {
  ALL_CATEGORIES,
  ALL_PRIORITIES,
  ALL_STATUSES,
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
} from './incidentLabels';
import type { IncidentCategory, IncidentPriority, IncidentStatus } from '../../types/extranet-api.types';

interface IncidentsFiltersProps {
  status: IncidentStatus[];
  onStatusChange: (next: IncidentStatus[]) => void;
  category: IncidentCategory[];
  onCategoryChange: (next: IncidentCategory[]) => void;
  priority: IncidentPriority[];
  onPriorityChange: (next: IncidentPriority[]) => void;
  totalCount: number;
}

/**
 * Filtres d'incidents — tous appliqués côté serveur depuis la v1.9.0.
 * Pas de recherche texte : `GET /v1/incidents` n'en expose pas.
 */
export const IncidentsFilters = ({
  status,
  onStatusChange,
  category,
  onCategoryChange,
  priority,
  onPriorityChange,
  totalCount,
}: IncidentsFiltersProps) => {
  const toggleStatus = (s: IncidentStatus) => {
    onStatusChange(status.includes(s) ? status.filter(x => x !== s) : [...status, s]);
  };

  const advancedCount = category.length + priority.length;
  const hasAnyFilter = status.length > 0 || advancedCount > 0;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-1.5 flex-wrap">
        {ALL_STATUSES.map(s => (
          <StatusChip key={s} status={s} active={status.includes(s)} onClick={() => toggleStatus(s)} />
        ))}
      </div>

      <AdvancedFiltersPopover
        category={category}
        onCategoryChange={onCategoryChange}
        priority={priority}
        onPriorityChange={onPriorityChange}
        activeCount={advancedCount}
      />

      <div className="ml-auto flex items-center gap-3 text-xs text-zinc-500">
        {hasAnyFilter ? (
          <button
            type="button"
            onClick={() => {
              onStatusChange([]);
              onCategoryChange([]);
              onPriorityChange([]);
            }}
            className="text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors"
          >
            Effacer les filtres
          </button>
        ) : null}
        <span className="font-mono tabular-nums">
          {totalCount} <span className="text-zinc-400">incident{totalCount > 1 ? 's' : ''}</span>
        </span>
      </div>
    </div>
  );
};

const StatusChip = ({ status, active, onClick }: { status: IncidentStatus; active: boolean; onClick: () => void }) => {
  const tokens = STATUS_COLORS[status];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors duration-100 active:scale-[0.98]',
        active
          ? 'bg-zinc-900 text-white border border-zinc-900'
          : 'bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
      ].join(' ')}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-white/80' : tokens.dot}`} aria-hidden />
      {STATUS_LABELS[status]}
    </button>
  );
};

const AdvancedFiltersPopover = ({
  category,
  onCategoryChange,
  priority,
  onPriorityChange,
  activeCount,
}: {
  category: IncidentCategory[];
  onCategoryChange: (next: IncidentCategory[]) => void;
  priority: IncidentPriority[];
  onPriorityChange: (next: IncidentPriority[]) => void;
  activeCount: number;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const toggleCategory = (c: IncidentCategory) => {
    onCategoryChange(category.includes(c) ? category.filter(x => x !== c) : [...category, c]);
  };
  const togglePriority = (p: IncidentPriority) => {
    onPriorityChange(priority.includes(p) ? priority.filter(x => x !== p) : [...priority, p]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={[
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium border transition-colors duration-100',
          activeCount > 0
            ? 'bg-zinc-900 text-white border-zinc-900'
            : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
        ].join(' ')}
      >
        <IconFilter width={12} height={12} />
        Filtres
        {activeCount > 0 ? <span className="font-mono tabular-nums">{activeCount}</span> : null}
        <IconChevronDown width={12} height={12} />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-72 bg-white border border-zinc-200 rounded-md shadow-md py-1.5 max-h-96 overflow-y-auto">
          <PopoverGroup label="Priorité">
            {ALL_PRIORITIES.map(p => (
              <CheckItem
                key={p}
                label={PRIORITY_LABELS[p]}
                checked={priority.includes(p)}
                onClick={() => togglePriority(p)}
              />
            ))}
          </PopoverGroup>
          <div className="my-1.5 border-t border-zinc-100" />
          <PopoverGroup label="Catégorie">
            {ALL_CATEGORIES.map(c => (
              <CheckItem
                key={c}
                label={CATEGORY_LABELS[c]}
                checked={category.includes(c)}
                onClick={() => toggleCategory(c)}
              />
            ))}
          </PopoverGroup>
        </div>
      ) : null}
    </div>
  );
};

const PopoverGroup = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="px-1.5">
    <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-400 px-2 pt-1 pb-1.5">{label}</p>
    <ul className="flex flex-col gap-0.5">{children}</ul>
  </div>
);

const CheckItem = ({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className="w-full min-h-8 px-2 py-1.5 flex items-center gap-2 rounded text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left"
    >
      <span
        className={[
          'h-3.5 w-3.5 rounded-sm border inline-flex items-center justify-center shrink-0',
          checked ? 'bg-zinc-900 border-zinc-900 text-white' : 'border-zinc-300 bg-white',
        ].join(' ')}
      >
        {checked ? <IconCheck width={10} height={10} strokeWidth={3} /> : null}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  </li>
);
